import { useCallback, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { Routes } from "constants/routes";
import { debounce, isEmpty, isEqual } from "lodash";
import { useReportsQueriesSelector } from "store";
import { ReportsQueries, RoutesValues } from "store/types";
import objectFilter from "utils/object-filter";

import { useDidUpdate, useNavigationHooks } from "..";
import { staticReportsQueries } from "../../constants/reports";

type Listen = () => void;

const reportsRoutes: RoutesValues[] = [
  Routes.snapshot,
  Routes.classic,
  Routes.comparison,
  Routes.timeline,
  Routes.dealReport,
  Routes.externalReport
];

export function useReportsQueries(): Listen {
  const [listen, setListen] = useState<boolean>(false);
  const queries = useReportsQueriesSelector();

  const { pathname } = useLocation();
  const { setParams, removeParams } = useNavigationHooks();

  const isReportRoute = useMemo(() => reportsRoutes.some((route) => matchPath(pathname, route)), [pathname]);

  const updateUrl = useCallback((incomingQueries: Partial<ReportsQueries<string>>) => {
    // Keys that should only appear in their own route, not in report URLs
    const excludeFromReportUrl = ["benchmarkTableColumns", "benchmarkIndexName", "graphBenchmark", "cashflowSetId"];

    // cleanQueries is diff between queries and static (system) queries
    const cleanQueries = objectFilter(
      incomingQueries,
      (value, key) =>
        !excludeFromReportUrl.includes(key as string) &&
        !isEqual(value, staticReportsQueries[key as keyof typeof staticReportsQueries])
    );

    // If this is default reports queries state remove them from URL
    if (isEmpty(cleanQueries)) {
      return removeParams(["reports"]);
    }

    // All any other case set queries to URL
    setParams({
      search: {
        reports: JSON.stringify({ ...cleanQueries })
      }
    });
  }, []);

  // Listen route
  useDidUpdate(() => {
    if (isReportRoute) {
      updateUrl(queries);
    } else {
      removeParams(["reports"]);
    }
  }, [isReportRoute, updateUrl]);

  const debouncedUpdateUrl = useMemo(() => {
    return debounce(updateUrl, 300);
  }, [updateUrl]);

  useDidUpdate(() => {
    if (!listen) return;
    if (!isReportRoute) return;
    debouncedUpdateUrl(queries);
  }, [queries]);

  return () => setListen(true);
}
